<template>
    <nav class="sidebar container">
        <div class="sidebar__swiper-container">
            <Swiper :options="swiperOptions" class="sidebar__swiper" @load="onSwiperLoad">
                <Link
                    v-for="(item, index) in sidebar.items"
                    :key="'sidebarItem' + index"
                    :link="item.link"
                    class="sidebar__link swiper-slide"
                >
                    {{ item.text }}
                </Link>

                <template v-slot:after> </template>
            </Swiper>

            <button v-show="swiper" class="btn sidebar__swiper-button-prev" @click.prevent="slideLeft">
                <IconSvg filepath="gallery/next.svg" class="sidebar__swiper-icon-prev text-primary" />
            </button>

            <button v-show="swiper" class="btn sidebar__swiper-button-next" @click.prevent="slideRight">
                <IconSvg filepath="gallery/next.svg" class="sidebar__swiper-icon-next text-primary" />
            </button>
        </div>

        <div class="sidebar__column">
            <div class="sidebar__title mb-2">
                {{ sidebar.title }}
            </div>

            <ul class="sidebar__items nav d-block">
                <li v-for="(item, index) in sidebar.items" :key="'sidebarItem' + index" class="sidebar__list-item">
                    <Link :link="item.link" class="sidebar__link">
                        {{ item.text }}
                    </Link>
                </li>
            </ul>
        </div>
    </nav>
</template>

<script>
import Sidebar from '@/models/Sidebar';

export default {
    name: 'Sidebar',
    components: { Swiper: () => import(/* webpackChunkName: "swiper" */ '@/components/swiper/Swiper') },
    data() {
        return {
            swiperOptions: {
                slidesPerView: 'auto',
                navigation: {
                    nextEl: '.sidebar__swiper-button-next',
                    prevEl: '.sidebar__swiper-button-prev'
                },
                observer: this.$cmsTool //needed for the admin-cms. Whenever user updates grid items, swiper should be updated
            },
            swiper: null
        };
    },

    props: {
        sidebar: {
            type: [Object, Sidebar],
            required: true
        }
    },

    methods: {
        onSwiperLoad(swiper) {
            this.swiper = swiper;
        },
        slideRight() {
            //default behavior is to slide only 1 slide
            this.swiper.translateTo(-screen.width);
        },
        slideLeft() {
            //default behavior is to slide only 1 slide
            this.swiper.translateTo(screen.width);
        }
    }
};
</script>

<style lang="scss">
@use '@/styles/variables';

$column-sidebar-after: 1350px;

// styles necessary for outside use for proper breadcrumbs placement
.sidebar {
    &__column {
        flex-basis: 0;
        flex-grow: 1;
        min-width: 0;
        max-width: 100%;
    }

    @media screen and (max-width: $column-sidebar-after - 1) {
        &__swiper-container {
            display: block;
        }

        &__column {
            display: none;
        }
    }

    @media screen and (min-width: $column-sidebar-after) {
        &__swiper-container {
            display: none;
        }

        &__column {
            display: block;
        }
    }
}
</style>

<style scoped lang="scss">
@use '@/styles/variables';
@use 'sass:map';

$column-sidebar-after: 1350px;

.sidebar {
    position: sticky;
    top: 0;
    overflow-y: auto;
    overflow-x: hidden;
    max-height: 100vh;
    align-self: flex-start;
    margin-bottom: 10px;

    @media screen and (min-width: $column-sidebar-after) {
        border: 1px solid variables.$border-color;
        box-shadow: 0 7px map-get(variables.$grid, 'tile-shadow-blur') 0 hsla(0, 0%, 0%, 0.1);
        padding: variables.$grid-padding;
        overflow-x: auto; //temporary to allow sidebar scroll
        // bootstrap col
        flex-basis: 0;
        flex-grow: 1;
        min-width: 0;
        max-width: 100%;

        &__items {
            list-style-type: '>';
            list-style-position: outside; //to show on the left side of multi-row items
            padding-left: 10px;
        }

        &__link {
            margin-left: 5px;
            max-width: 100%;
            white-space: normal; //to allow long links break
        }
    }

    @media screen and (max-width: $column-sidebar-after - 1) {
        $sidebar-mobile-height: 30px;
        z-index: map.get(variables.$z-indices, 'header-mobile') - 1;
        position: relative;
        top: 0;
        padding-top: 0;

        &__link {
            padding: 0 12px;
            border: 1px solid variables.$border-color;
            background-color: variables.$aside-darker-bg;
            white-space: nowrap;
            margin-right: 5px;
            width: auto;
            height: $sidebar-mobile-height; //helps display sidebar properly before swiper init
            display: inline-flex; //helps display sidebar properly before swiper init
            align-items: center;
        }

        .swiper-button-disabled {
            display: none;
        }

        &__swiper-button-next,
        &__swiper-button-prev {
            position: absolute;
            /*transform: translateY(-50%);*/
            line-height: 1;
            font-size: 0;
            z-index: 2;
            top: 0;
            height: $sidebar-mobile-height;
            padding: 0 variables.$grid-padding;

            &:focus,
            &:active {
                outline: 0;
                box-shadow: none;
            }
        }

        &__swiper-button-next {
            background: linear-gradient(90deg, transparent, var(--white) 40%);
        }

        &__swiper-button-prev {
            background: linear-gradient(90deg, var(--white) 40%, transparent);
        }

        &__swiper-button-next {
            right: 0;
            padding-left: 30px;
        }

        &__swiper-button-prev {
            left: 0;
            padding-right: 30px;
        }

        &__swiper-icon-next,
        &__swiper-icon-prev {
            font-size: 20px;
        }

        &__swiper-icon-prev {
            transform: rotate(180deg);
        }
    }

    &__swiper {
        margin: 0 (-(variables.$grid-padding));
        padding: 0 variables.$grid-padding;
        white-space: nowrap; //helps display sidebar properly before swiper init
    }

    &__title {
        font-weight: bold;
    }

    &__link {
        color: var(--body-color);
    }
}
</style>
